import type { GetPositionsRow, UserPortfolioPositionWire } from '@morfi/backend-sdk-v2/client';
import { isDustD6 } from '@/lib/validation/dust';

/**
 * Positive token balance that still requires lifecycle-aware valuation. This
 * includes terminal losers: they resolve to an exact zero value rather than a
 * quote, while backend `isOpen` remains only a legacy spot-mark flag.
 */
export function hasQuotableOutcomeBalance(row: GetPositionsRow): boolean {
  return /^\d+$/.test(row.balance) && BigInt(row.balance) > BigInt(0);
}

/**
 * UI lifecycle state is distinct from valuation eligibility. A positive
 * terminal loser still needs an exact terminal-zero mark, but it belongs in
 * the closed/resolved group rather than being presented as a reopened trade.
 * Legacy `sold_out` rows with a positive balance remain active: that state was
 * derived from a linear dust mark and the live pool quote is authoritative.
 */
export function hasActiveOutcomeBalance(row: GetPositionsRow): boolean {
  return hasQuotableOutcomeBalance(row) && row.resultState !== 'lost';
}

/** Positive indexed display balance without probing the legacy `balance` alias. */
export function hasPositiveSnapshotOutcomeBalance(
  row: Pick<UserPortfolioPositionWire, 'balanceD6'>,
): boolean {
  return /^(?:[1-9]\d*)$/.test(row.balanceD6);
}

/** Snapshot display equivalent of `hasActiveOutcomeBalance`. */
export function hasActiveSnapshotOutcomeBalance(
  row: Pick<UserPortfolioPositionWire, 'balanceD6' | 'resultState'>,
): boolean {
  return hasPositiveSnapshotOutcomeBalance(row) && row.resultState !== 'lost';
}

/**
 * Market-detail visibility for a snapshot position. Fully exited positions
 * stay visible when they contain a meaningful realised result, investment, or
 * prize; only zero-balance dust ghosts disappear.
 */
export function isVisibleSnapshotMarketPosition(
  row: Pick<
    UserPortfolioPositionWire,
    'balanceD6' | 'costBasisD6' | 'prizeState' | 'realizedPnlD6'
  >,
): boolean {
  return (
    hasPositiveSnapshotOutcomeBalance(row) ||
    row.prizeState !== 'none' ||
    !isDustD6(row.realizedPnlD6) ||
    !isDustD6(row.costBasisD6)
  );
}

/**
 * A `sold_out` position the viewer never meaningfully invested in — a true
 * "ghost" the market-detail Position card hides rather than surfacing a "$0.00"
 * row with trade chips (MOR-619). It must satisfy all of: fully exited
 * (`sold_out`), no prize, **no meaningful realised PnL**, AND **no meaningful
 * remaining or released cost basis**.
 *
 * A real round trip — the viewer bought a genuine amount and sold it back near
 * break-even — is NOT a ghost and stays visible as a closed position, so the
 * viewer sees the trade happened even at $0.00 realised. A full exit correctly
 * has zero remaining `costBasisD6`; the distinguishing lifetime signal is
 * `realizedCostBasisGrossD6`, which records the gross basis released by realised
 * trades.
 *
 * `sold_out` already implies a dust spot residual (backend: spot <
 * `USDC_DUST_THRESHOLD_D6`, $0.02) and never carries a prize, so the extra gates
 * are "realised PnL is dust", "remaining basis is dust", AND "released basis is
 * dust". `isDustD6` uses the $0.02 floor, so a `realizedPnlD6` of `6` ($0.000006
 * → renders as $0.00) counts as not meaningful.
 */
export function isClosedDustResidual(row: GetPositionsRow): boolean {
  return (
    row.resultState === 'sold_out' &&
    !hasQuotableOutcomeBalance(row) &&
    row.prizeState === 'none' &&
    isDustD6(row.realizedPnlD6) &&
    isDustD6(row.costBasisD6) &&
    isDustD6(row.realizedCostBasisGrossD6)
  );
}
